import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { CheckCircle, AlertTriangle, XCircle, Info, Clock } from 'lucide-react'

interface ValidationError {
  field: string
  message: string
  severity: 'error' | 'warning' | 'info'
}

interface ValidationFeedbackProps {
  errors: ValidationError[]
  isValidating?: boolean
  className?: string
}

export const ValidationFeedback = ({ 
  errors, 
  isValidating = false,
  className = '' 
}: ValidationFeedbackProps) => {
  console.log('[ValidationFeedback] Rendering with:', { 
    errorsCount: errors.length, 
    isValidating,
    errors 
  })

  const errorsByType = {
    error: errors.filter(e => e.severity === 'error'),
    warning: errors.filter(e => e.severity === 'warning'),
    info: errors.filter(e => e.severity === 'info'),
  }

  const getIcon = (severity: string) => {
    switch (severity) {
      case 'error': return <XCircle className="h-4 w-4 text-destructive" />
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-500" />
      case 'info': return <Info className="h-4 w-4 text-blue-500" />
      default: return null
    }
  }

  const getSeverityBadge = (severity: string) => {
    const variants = {
      error: 'destructive' as const,
      warning: 'secondary' as const,
      info: 'outline' as const,
    }
    
    return (
      <Badge variant={variants[severity as keyof typeof variants]}>
        {severity.toUpperCase()}
      </Badge>
    )
  }

  if (isValidating) {
    return (
      <Card className={className}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Clock className="h-4 w-4 animate-pulse text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Validating your request...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (errors.length === 0) {
    return (
      <Alert className={`border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 ${className}`}>
        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
        <AlertDescription className="text-green-800 dark:text-green-200">
          All validation checks passed - ready to submit
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {Object.entries(errorsByType).map(([severity, severityErrors]) => {
        if (severityErrors.length === 0) return null

        const variant = severity === 'error' ? 'destructive' : 'default'
        
        return (
          <Alert key={severity} variant={variant}>
            <div className="flex items-start gap-2">
              {getIcon(severity)}
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  {getSeverityBadge(severity)}
                  <span className="text-sm font-medium">
                    {severityErrors.length} {severity}(s) found
                  </span>
                </div>
                <ul className="space-y-1 text-sm">
                  {severityErrors.map((error, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="font-medium text-xs mt-0.5 opacity-60">
                        {error.field}:
                      </span>
                      <span>{error.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Alert>
        )
      })}
    </div>
  )
}

interface BalanceValidationProps {
  availableBalance: number
  requestedDays: number
  policyName?: string
  warnings?: string[]
  canSubmit?: boolean
}

export const BalanceValidation = ({ 
  availableBalance, 
  requestedDays, 
  policyName,
  warnings = [],
  canSubmit = true
}: BalanceValidationProps) => {
  const balancePercentage = Math.max(0, Math.min(100, (availableBalance / 30) * 100)) // Assuming 30 is max
  const wouldExceed = requestedDays > availableBalance
  const remainingBalance = availableBalance - requestedDays

  console.log('[BalanceValidation] Rendering with:', {
    availableBalance,
    requestedDays,
    policyName,
    wouldExceed,
    remainingBalance,
    canSubmit
  })

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Available Balance</span>
            <span className="text-muted-foreground">
              {availableBalance} days
              {policyName && ` (${policyName})`}
            </span>
          </div>
          <Progress value={balancePercentage} className="w-full" />
        </div>

        {requestedDays > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Requested:</span>
              <span className={wouldExceed ? 'text-destructive font-medium' : ''}>
                {requestedDays} days
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Remaining after request:</span>
              <span className={remainingBalance < 0 ? 'text-destructive font-medium' : 'text-green-600 dark:text-green-400'}>
                {remainingBalance} days
              </span>
            </div>
          </div>
        )}

        {wouldExceed && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription className="font-medium">
              Cannot submit: Request exceeds available balance by {requestedDays - availableBalance} days
            </AlertDescription>
          </Alert>
        )}

        {!wouldExceed && !canSubmit && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              Cannot submit: Please resolve validation errors above
            </AlertDescription>
          </Alert>
        )}

        {warnings.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1">
                {warnings.map((warning, index) => (
                  <li key={index} className="text-sm">{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
